/**
 * 라우트 → 권한 매핑 테이블 (single source of truth)
 *
 * 각 기능 페이지의 접근에 필요한 "조회(read)" 권한을 정의한다.
 * - 메뉴(HomeNavBar, AppHeader) 필터링
 * - 라우트 가드(withPermissionGuard)
 *
 * 양쪽 모두 이 파일의 매핑을 사용하므로, 새 페이지 추가 시 이 파일만 갱신하면 된다.
 *
 * ─── 규칙 ─────────────────────────────────────────────
 * - 값이 문자열이면 그 권한을 보유해야 접근 가능 (useAuth.hasPermission 사용)
 * - 값이 null 이면 모두에게 공개 (조회 권한 불필요)
 * - 값이 'ADMIN_ONLY' 이면 USERID === 'admin' 계정만 접근 가능
 *
 * 백엔드 backend/src/modules/auth/auth.dto.js의 LEGACY_FLAG_MAP과
 * 동기화되어야 한다. LEGACY_FLAG_MAP에 정의된 'module:entity:read' 권한을 사용한다.
 */

/** 관리자 전용 sentinel 값 */
export const ADMIN_ONLY = 'ADMIN_ONLY';

/**
 * 정확 경로 → 필요 권한 매핑
 * prefix 매칭이 아닌 정확한 pathname 매칭이다.
 */
export const ROUTE_PERMISSIONS = {
  // ─── 홈 / 검색 ────────────────────────────────────
  '/': null,
  '/search': null,

  // ─── PRD (농약) ───────────────────────────────────
  '/prd/pesticides': 'prd:pesticide:read',
  '/prd/mrls': 'prd:mrl:read',
  '/prd/foods': 'prd:food:read',
  '/prd/adi': 'prd:adi:read',
  '/prd/using': 'prd:using:read',
  '/prd/analysis': 'prd:analysis:read',
  '/prd/analysis-l': 'prd:analysis:read',
  '/prd/reports': 'prd:report:read',
  '/prd/progress': 'doc:related:read',
  '/prd/proposals': 'prd:proposal:read',

  // ─── VD (동물용의약품) ────────────────────────────
  '/vd/drugs': 'vd:pesticide:read',
  '/vd/mrls': 'vd:mrl:read',
  '/vd/foods': 'vd:food:read',
  '/vd/adi': 'vd:adi:read',
  '/vd/using': 'vd:using:read',
  '/vd/analysis': 'vd:analysis:read',

  // ─── STD (기준규격) ──────────────────────────────
  '/std/standards': 'std:standard:read',
  '/std/applications': 'std:application:read',
  '/std/requests': 'std:request:read',

  // ─── DOC (게시판/관련자료) ────────────────────────
  '/doc/prd-board': null,
  '/doc/vd-board': null,
  '/doc/articles': null,
  '/doc/related': null,

  // ─── Admin (관리자 전용 — USERID='admin') ────────
  '/admin/dashboard': ADMIN_ONLY,
  '/admin/users': ADMIN_ONLY,
  '/admin/auth-templates': ADMIN_ONLY,
  '/admin/visitors': ADMIN_ONLY,
  '/admin/audit': ADMIN_ONLY,
  '/admin/workflow': ADMIN_ONLY,
  '/admin/notifications': ADMIN_ONLY,
  '/admin/batch': ADMIN_ONLY,
  '/admin/quality': ADMIN_ONLY,
  '/admin/popups': ADMIN_ONLY,
  '/admin/change-password': ADMIN_ONLY,

  // ─── Auth (인증) ──────────────────────────────────
  '/auth/change-password': null, // 로그인한 사용자 본인의 비밀번호 변경
};

/**
 * prefix 기반 매칭 — 정확 경로에 없지만 prefix로 공통 권한이 적용되는 경로
 * 예: /admin/master/* 는 모두 ADMIN_ONLY
 */
export const ROUTE_PERMISSION_PREFIXES = [
  { prefix: '/admin/master/', permission: ADMIN_ONLY },
  { prefix: '/admin/', permission: ADMIN_ONLY }, // fallback: /admin/* 전부 admin 전용
  { prefix: '/chpwd/', permission: null },       // 1회용 코드 기반 — 공개
];

/**
 * 경로에서 필요한 권한을 조회한다.
 * @param {string} pathname - React Router location.pathname
 * @returns {string|null} 권한 문자열, 'ADMIN_ONLY', 또는 null(공개)
 */
export function getRoutePermission(pathname) {
  if (!pathname) return null;

  // 1) 정확 매칭
  if (Object.prototype.hasOwnProperty.call(ROUTE_PERMISSIONS, pathname)) {
    return ROUTE_PERMISSIONS[pathname];
  }

  // 2) prefix 매칭
  for (const { prefix, permission } of ROUTE_PERMISSION_PREFIXES) {
    if (pathname.startsWith(prefix)) return permission;
  }

  // 3) 정의 없음 → 공개 기본값
  return null;
}

/**
 * 특정 액션(read/write/delete)에 대응하는 권한 문자열을 반환한다.
 *
 * 읽기 권한이 'module:entity:read' 형태이면 액션을 교체해서 반환:
 *   getActionPermission('prd:pesticide:read', 'write')  → 'prd:pesticide:write'
 *   getActionPermission('prd:pesticide:read', 'delete') → 'prd:pesticide:delete'
 *
 * null(공개) 이면 null 반환 — 공개 페이지는 쓰기/삭제 권한이 없는 것으로 처리.
 * ADMIN_ONLY 이면 ADMIN_ONLY 반환 — admin이면 모두 허용.
 *
 * @param {string|null} readPermission - 'module:entity:read' 형식의 읽기 권한
 * @param {'read'|'write'|'delete'} action - 확인할 액션
 * @returns {string|null} 해당 액션 권한 문자열, ADMIN_ONLY, 또는 null
 */
export function getActionPermission(readPermission, action) {
  if (readPermission === null || readPermission === undefined) return null;
  if (readPermission === ADMIN_ONLY) return ADMIN_ONLY;
  const parts = String(readPermission).split(':');
  if (parts.length !== 3) return null;
  return `${parts[0]}:${parts[1]}:${action}`;
}
